import { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useParams, Navigate } from 'react-router-dom';
import { Search, LayoutGrid, Settings, User, LogOut, Lock } from 'lucide-react';
import { AuthProvider, useAuth } from './hooks/useAuth';
import { supabase } from './lib/supabase';
import { SpaceList } from './components/SpaceList';
import { FolderBrowser } from './components/FolderBrowser';
import { FilePreview } from './components/FilePreview';
import { RouterGate } from './components/RouterGate';
import { SpaceManagement } from './components/SpaceManagement';
import { SyncProgressModal } from './components/SyncProgressModal';
import type { DocumentSpace, DriveItem } from '@/shared';
import { buildInfo } from '../shared/generated/build-info';

const API_URL = '';

interface PathSegment {
  id: string | null;
  name: string;
}

function AppContent() {
  const { user, redirectToPortal, signOut } = useAuth();
  const { spaceSlug, folderId, fileId } = useParams();
  const navigate = useNavigate();

  const [spaces, setSpaces] = useState<DocumentSpace[]>([]);
  const [loadingSpaces, setLoadingSpaces] = useState(true);
  const [apiOnline, setApiOnline] = useState<boolean>(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const checkApiStatus = async () => {
    try {
      const res = await fetch(`${API_URL}/health`);
      if (res.ok) {
        setApiOnline(true);
      } else {
        setApiOnline(false);
      }
    } catch {
      setApiOnline(false);
    }
  };

  useEffect(() => {
    checkApiStatus();
  }, []);
  const [activeSpace, setActiveSpace] = useState<DocumentSpace | null>(null);
  const [activeItems, setActiveItems] = useState<DriveItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [pathHistory, setPathHistory] = useState<Array<{ id: string | null; name: string }>>([]);
  const [previewFile, setPreviewFile] = useState<DriveItem | null>(null);
  const [lastSyncedAt, setLastSyncedAt] = useState<string | null>(null);
  const [isSyncModalOpen, setIsSyncModalOpen] = useState(false);
  const [syncTargetFolderId, setSyncTargetFolderId] = useState<string | null>(null);
  const [syncTargetFolderName, setSyncTargetFolderName] = useState<string>('');
  const loadedFolderRef = useRef<{ spaceSlug: string; folderId: string | null } | null>(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Search & Profile States
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [roles, setRoles] = useState<string[]>([]);
  const [participantType, setParticipantType] = useState<string | null>(null);
  const [residentSubtype, setResidentSubtype] = useState<string | null>(null);
  const [requestedAffiliation, setRequestedAffiliation] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setRoles([]);
      setParticipantType(null);
      setResidentSubtype(null);
      setRequestedAffiliation(null);
      return;
    }

    // Fetch global user roles
    supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .then(({ data }) => {
        const fetchedRoles = data?.map(r => r.role) || [];
        setRoles(fetchedRoles);
        setIsAdmin(fetchedRoles.includes('admin'));
      });

    // Fetch user profiles details
    supabase
      .from('profiles')
      .select('participant_type, resident_subtype, requested_affiliation')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setParticipantType(data.participant_type);
          setResidentSubtype(data.resident_subtype);
          setRequestedAffiliation(data.requested_affiliation);
        }
      });
  }, [user]);

  // Load spaces available to the user
  const fetchSpaces = async () => {
    setLoadingSpaces(true);
    try {
      const res = await fetch(`${API_URL}/api/spaces`, { credentials: 'include' });
      if (res.status === 401 && user) {
        redirectToPortal();
        return;
      }
      const data = await res.json();
      if (Array.isArray(data)) {
        setSpaces(data);
      }
      setApiOnline(true);
    } catch (error) {
      console.error('Failed to load spaces:', error);
      setApiOnline(false);
    } finally {
      setLoadingSpaces(false);
    }
  };

  useEffect(() => {
    fetchSpaces();
  }, [user]);

  // Main URL-driven navigation content loading effect
  useEffect(() => {
    if (loadingSpaces) return;

    if (!spaceSlug) {
      // Dashboard view
      setActiveSpace(null);
      setActiveItems([]);
      setPathHistory([]);
      setPreviewFile(null);
      loadedFolderRef.current = null;
      setErrorMsg(null);
      return;
    }

    const space = spaces.find((s) => s.slug === spaceSlug);
    if (!space) {
      if (!user) {
        redirectToPortal();
      } else {
        setErrorMsg('Access denied or workspace not found');
      }
      return;
    }

    setErrorMsg(null);
    setActiveSpace(space);

    const loadContents = async () => {
      console.log('[loadContents] Running with:', { spaceSlug, folderId, fileId, loadedFolder: loadedFolderRef.current });
      try {
        if (fileId) {
          // 1. Direct deep-link to a file preview
          // Set preview file immediately from activeItems if available, or set a loading stub
          const localItem = activeItems.find(item => item.id === fileId);
          if (localItem) {
            setPreviewFile(localItem);
          } else {
            setPreviewFile({ id: fileId, name: 'Loading...', mimeType: '' } as any);
          }

          // Fetch file info
          console.log('[loadContents] Fetching file info for:', fileId);
          const infoRes = await fetch(`${API_URL}/api/files/${fileId}/info?spaceSlug=${space.slug}`, { credentials: 'include' });
          if (infoRes.status === 401) {
            redirectToPortal();
            return;
          }
          if (!infoRes.ok) {
            throw new Error('Failed to retrieve file details');
          }
          const fileMeta: DriveItem = await infoRes.json();
          setPreviewFile(fileMeta);

          // 2. Determine if the parent folder is already loaded
          const parentId = fileMeta.navigationParent || null;
          const isFolderLoaded = loadedFolderRef.current &&
            loadedFolderRef.current.spaceSlug === space.slug &&
            loadedFolderRef.current.folderId === parentId;

          console.log('[loadContents] File parent folder:', parentId, 'isFolderLoaded:', isFolderLoaded);

          if (!isFolderLoaded) {
            setLoadingItems(true);
            let folderUrl = `${API_URL}/api/spaces/${space.slug}`;
            if (parentId && parentId !== space.drive_folder_id) {
              folderUrl = `${API_URL}/api/spaces/${space.slug}/items/${parentId}`;
            }

            console.log('[loadContents] Fetching folder context:', folderUrl);
            const folderRes = await fetch(folderUrl, { credentials: 'include' });
            if (!folderRes.ok) throw new Error('Failed to load background directory');
            const folderData = await folderRes.json();

            setActiveItems(folderData.items || []);
            setLastSyncedAt(folderData.lastSyncedAt || null);
            setApiOnline(true);

            // 3. Set context-aware breadcrumbs
            if (fileMeta.navigation?.breadcrumb) {
              setPathHistory(fileMeta.navigation.breadcrumb.map((b: any) => ({
                id: b.type === 'space' ? null : b.id,
                name: b.name,
              })));
            } else if (parentId && parentId !== space.drive_folder_id) {
              setPathHistory([
                { id: null, name: space.name },
                { id: parentId, name: folderData.folderName || 'Folder' }
              ]);
            } else {
              setPathHistory([{ id: null, name: space.name }]);
            }

            loadedFolderRef.current = { spaceSlug: space.slug, folderId: parentId };
            setLoadingItems(false);
          } else {
            console.log('[loadContents] Skipping folder fetch, folder already loaded.');
            // Already loaded, just make sure we set breadcrumbs if they exist
            if (fileMeta.navigation?.breadcrumb) {
              setPathHistory(fileMeta.navigation.breadcrumb.map((b: any) => ({
                id: b.type === 'space' ? null : b.id,
                name: b.name,
              })));
            }
          }
        } else {
          // 4. Loading folder contents directly (space root or subfolder)
          setPreviewFile(null);

          const targetFolderId = folderId || null;
          const isFolderLoaded = loadedFolderRef.current &&
            loadedFolderRef.current.spaceSlug === space.slug &&
            loadedFolderRef.current.folderId === targetFolderId;

          console.log('[loadContents] Direct folder target:', targetFolderId, 'isFolderLoaded:', isFolderLoaded);

          if (!isFolderLoaded) {
            setLoadingItems(true);
            let url = `${API_URL}/api/spaces/${space.slug}`;
            if (folderId) {
              url = `${API_URL}/api/spaces/${space.slug}/items/${folderId}`;
            }

            console.log('[loadContents] Fetching folder contents:', url);
            const res = await fetch(url, { credentials: 'include' });
            if (res.status === 401) {
              redirectToPortal();
              return;
            }
            if (!res.ok) {
              throw new Error('Failed to retrieve folder contents');
            }

            const data = await res.json();
            setActiveItems(data.items || []);
            setLastSyncedAt(data.lastSyncedAt || null);
            setApiOnline(true);

            // Preserve path history recursively or fallback to list
            if (data.navigation?.breadcrumb) {
              setPathHistory(data.navigation.breadcrumb.map((b: any) => ({
                id: b.type === 'space' ? null : b.id,
                name: b.name,
              })));
            } else if (folderId) {
              setPathHistory((prev) => {
                const idx = prev.findIndex((x) => x.id === folderId);
                if (idx !== -1) {
                  return prev.slice(0, idx + 1);
                } else {
                  return [
                    { id: null, name: space.name },
                    { id: folderId, name: data.folderName || 'Folder' }
                  ];
                }
              });
            } else {
              setPathHistory([{ id: null, name: space.name }]);
            }

            loadedFolderRef.current = { spaceSlug: space.slug, folderId: targetFolderId };
            setLoadingItems(false);
          } else {
            console.log('[loadContents] Skipping direct folder fetch, folder already loaded.');
          }
        }
      } catch (err: any) {
        console.error('[loadContents] Error:', err);
        setApiOnline(false);
        setErrorMsg(err.message || 'Access Denied. You do not have permission to view this.');
        setPreviewFile(null);
        setLoadingItems(false);
      }
    };

    loadContents();
  }, [spaceSlug, folderId, fileId, spaces, loadingSpaces, user]);

  const handleSelectSpace = (space: DocumentSpace) => {
    const rules = space.access_rules || {};
    const isPrivate = (rules.participant_types?.length || 0) > 0 || (rules.roles?.length || 0) > 0;
    if (isPrivate && !user) {
      redirectToPortal();
      return;
    }
    navigate(`/${space.slug}`);
  };

  const handleNavigateFolder = (targetFolderId: string | null, _folderName: string) => {
    if (!spaceSlug) return;
    if (targetFolderId === null) {
      navigate(`/${spaceSlug}`);
    } else {
      navigate(`/${spaceSlug}/folder/${targetFolderId}`);
    }
  };

  const handlePreviewFile = (item: DriveItem) => {
    if (!spaceSlug) return;
    navigate(`/${spaceSlug}/file/${item.id}`);
  };

  const handleClosePreview = () => {
    const parentId = previewFile?.navigationParent;
    setPreviewFile(null);
    if (!spaceSlug) return;
    if (parentId && activeSpace && parentId !== activeSpace.drive_folder_id) {
      navigate(`/${spaceSlug}/folder/${parentId}`);
    } else if (folderId) {
      navigate(`/${spaceSlug}/folder/${folderId}`);
    } else {
      navigate(`/${spaceSlug}`);
    }
  };

  const previewableFiles = activeItems.filter(item => !item.isFolder);
  const currentFileIndex = previewableFiles.findIndex(item => item.id === fileId);

  const handleNextFile = () => {
    if (currentFileIndex !== -1 && currentFileIndex < previewableFiles.length - 1) {
      handlePreviewFile(previewableFiles[currentFileIndex + 1]);
    }
  };

  const handlePrevFile = () => {
    if (currentFileIndex > 0) {
      handlePreviewFile(previewableFiles[currentFileIndex - 1]);
    }
  };

  const handleBackToDashboard = () => {
    navigate('/');
  };

  const handleRefreshFolder = (targetFolderId: string | null, targetFolderName: string) => {
    if (!activeSpace) return;
    setSyncTargetFolderId(targetFolderId);
    setSyncTargetFolderName(targetFolderName);
    setIsSyncModalOpen(true);
  };

  const handleSyncComplete = () => {
    loadedFolderRef.current = null;
    if (spaceSlug) {
      let url = `${API_URL}/api/spaces/${spaceSlug}`;
      if (folderId) {
        url = `${API_URL}/api/spaces/${spaceSlug}/items/${folderId}`;
      }
      fetch(url, { credentials: 'include' })
        .then(res => res.json())
        .then(data => {
          setActiveItems(data.items || []);
          setLastSyncedAt(data.lastSyncedAt || null);
        })
        .catch(console.error);
    }
  };

  const getUserTags = () => {
    const tags: React.ReactNode[] = [];

    // 1. Classification Tag
    if (participantType === 'resident') {
      const isOwner = residentSubtype === 'owner';
      const label = isOwner ? 'Resident (Owner)' : 'Resident (Renter)';
      const className = isOwner ? 'badge-owner' : 'badge-renter';
      tags.push(
        <span key="resident-type" className={`badge ${className}`}>
          {label}
        </span>
      );
    } else if (participantType === 'non_resident') {
      const affiliation = requestedAffiliation || 'Staff';
      const formattedAffiliation = affiliation.charAt(0).toUpperCase() + affiliation.slice(1);
      tags.push(
        <span key="non-resident-type" className="badge badge-staff">
          Non-Resident ({formattedAffiliation})
        </span>
      );
    }

    // 2. Global Role Tags
    if (roles && Array.isArray(roles)) {
      roles.forEach((role) => {
        if (role === 'admin') {
          tags.push(
            <span key="role-admin" className="badge badge-admin">
              Global Admin
            </span>
          );
        } else if (role === 'resident_verifier') {
          tags.push(
            <span key="role-verifier" className="badge badge-verifier">
              Verifier
            </span>
          );
        } else if (role === 'platform_moderator') {
          tags.push(
            <span key="role-moderator" className="badge badge-moderator">
              Moderator
            </span>
          );
        } else if (role === 'committee') {
          tags.push(
            <span key="role-committee" className="badge badge-owner">
              Committee
            </span>
          );
        }
      });
    }

    return tags;
  };

  const getInitials = (email?: string | null) => {
    return email?.charAt(0).toUpperCase() || null;
  };

  return (
    <div className="layout-wrapper">
      {/* Navigation Header */}
      <header className="navbar">
        <div className="navbar-container">
          {/* Brand Logo & Title */}
          <div className={`brand-container ${isSearchFocused ? 'hidden-on-search-focus' : ''}`}>
            <a href="/" className="brand" onClick={(e) => { e.preventDefault(); handleBackToDashboard(); }}>
              <div className="brand-logo-icon">
                <img src="/favicon.svg" alt="Logo" style={{ width: '100%', height: '100%', display: 'block' }} />
              </div>
              <span className="brand-text">Community Documents</span>
            </a>
            <span
              className="version-badge hidden-on-mobile"
              title={`Branch: ${buildInfo.gitBranch}\nCommit: ${buildInfo.gitCommitSha}\nBuilt: ${new Date(buildInfo.buildTimestamp).toLocaleString()}\nEnv: ${buildInfo.environment}`}
            >
              v{buildInfo.version}
            </span>
          </div>

          {/* Center Search - Inline full width responsive */}
          <div className="search-container">
            <div className="search-wrapper">
              <span className="search-icon"><Search size={16} /></span>
              <input
                type="search"
                placeholder="Search files by keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => setIsSearchFocused(true)}
                onBlur={() => setIsSearchFocused(false)}
                className="form-control search-input"
              />
            </div>
          </div>

          {/* Right Action Items */}
          <div className={`nav-actions ${isSearchFocused ? 'hidden-on-search-focus' : ''}`} ref={mobileMenuRef}>
            {/* Portal link */}
            <a
              href={import.meta.env.VITE_PORTAL_URL || 'https://portal.sakura3.id'}
              className="btn btn-secondary portal-btn"
            >
              <LayoutGrid size={15} /> <span className="desktop-only-text">Portal</span>
            </a>

            {/* Config button (Admin space management) */}
            {user && isAdmin && (
              <button
                className="btn btn-secondary config-btn"
                onClick={() => navigate('/admin/document-spaces')}
                title="Manage Document Spaces"
              >
                <Settings size={15} /> <span className="desktop-only-text">Config</span>
              </button>
            )}

            {/* Desktop inline controls */}
            {!user && (
              <div className="desktop-nav-actions">
                <button className="btn btn-primary" onClick={redirectToPortal}>
                  Sign In with Portal
                </button>
              </div>
            )}

            {/* Unified Avatar (Always visible, rightmost) */}
            <div className="avatar-container">
              <button
                className="avatar-btn"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="User menu"
              >
                {(user?.user_metadata?.avatar_url || user?.user_metadata?.picture) ? (
                  <img
                    src={user.user_metadata.avatar_url || user.user_metadata.picture}
                    alt="Avatar"
                    className="avatar-img"
                  />
                ) : (
                  getInitials(user?.email) ?? <User size={16} />
                )}
              </button>

              {mobileMenuOpen && (
                <div className="mobile-dropdown-menu">
                  {user ? (
                    <>
                      <div className="mobile-dropdown-header">
                        <div className="dropdown-avatar-thumb">
                          {(user.user_metadata?.avatar_url || user.user_metadata?.picture) ? (
                            <img src={user.user_metadata.avatar_url || user.user_metadata.picture} alt="User" />
                          ) : (
                            <span>{getInitials(user.email)}</span>
                          )}
                        </div>
                        <div className="dropdown-user-details">
                          <span className="dropdown-user-name">
                            {user.user_metadata?.full_name || user.email?.split('@')[0] || "User"}
                          </span>
                          <span className="dropdown-user-email">{user.email}</span>
                          <div className="dropdown-tags-wrapper">
                            {getUserTags()}
                          </div>
                        </div>
                      </div>
                      <div className="dropdown-divider" />
                      <button 
                        className="dropdown-item dropdown-item-danger" 
                        onClick={() => {
                          setMobileMenuOpen(false);
                          signOut();
                        }}
                      >
                        <LogOut size={15} /> Sign out
                      </button>
                    </>
                  ) : (
                    <div style={{ padding: '0.5rem' }}>
                      <button className="btn btn-primary w-100" onClick={() => { setMobileMenuOpen(false); redirectToPortal(); }}>
                        Sign In with Portal
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      <div className="app-container">
        {!apiOnline && (
          <div className="api-warning-banner">
            ⚠️ <strong>Connection Warning:</strong> The documents API service is currently unreachable. Some files and folders may fail to load.
          </div>
        )}

        {/* Main Content Area */}
        <main>
          {spaceSlug ? (
            errorMsg ? (
              <div className="glass-panel text-center" style={{ padding: '3rem' }}>
                <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '1rem', color: 'var(--text-muted)' }}>
                  <Lock size={48} />
                </div>
                <h3>{errorMsg}</h3>
                <button className="btn btn-primary mt-4" onClick={handleBackToDashboard}>
                  Return to Hub
                </button>
              </div>
            ) : loadingItems ? (
              <div className="text-center" style={{ padding: '5rem 0' }}>
                <div className="loading-spinner" style={{ margin: '0 auto 1.5rem' }}></div>
                <p style={{ color: 'var(--text-secondary)' }}>Retrieving Drive folders...</p>
              </div>
            ) : activeSpace ? (
              <FolderBrowser
                spaceName={activeSpace.name}
                spaceSlug={activeSpace.slug}
                items={activeItems.filter(item =>
                  item.name.toLowerCase().includes(searchQuery.toLowerCase())
                )}
                pathHistory={pathHistory}
                isAdmin={isAdmin}
                lastSyncedAt={lastSyncedAt}
                onNavigateFolder={handleNavigateFolder}
                onPreviewFile={handlePreviewFile}
                onBackToDashboard={handleBackToDashboard}
                onRefreshFolder={handleRefreshFolder}
              />
            ) : null
          ) : loadingSpaces ? (
            <div className="text-center" style={{ padding: '5rem 0' }}>
              <div className="loading-spinner" style={{ margin: '0 auto 1.5rem' }}></div>
              <p style={{ color: 'var(--text-secondary)' }}>Loading workspaces...</p>
            </div>
          ) : (
            <SpaceList spaces={spaces} onSelectSpace={handleSelectSpace} />
          )}
        </main>

        {/* File Previewer Overlay */}
        {previewFile && activeSpace && (
          <FilePreview
            fileId={previewFile.id}
            fileName={previewFile.name}
            mimeType={previewFile.mimeType}
            onClose={handleClosePreview}
            onNext={currentFileIndex < previewableFiles.length - 1 ? handleNextFile : undefined}
            onPrev={currentFileIndex > 0 ? handlePrevFile : undefined}
          />
        )}
        {/* Realtime Sync Progress Modal Overlay */}
        {activeSpace && (
          <SyncProgressModal
            isOpen={isSyncModalOpen}
            spaceSlug={activeSpace.slug}
            spaceName={activeSpace.name}
            targetFolderId={syncTargetFolderId}
            targetFolderName={syncTargetFolderName}
            apiUrl={API_URL}
            onClose={() => setIsSyncModalOpen(false)}
            onSyncComplete={handleSyncComplete}
          />
        )}
      </div>
    </div>
  );
}

function RouteWrapper() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);

  // Check if current user is admin in Supabase user_roles
  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      return;
    }
    supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', user.id)
      .then(({ data }) => {
        const hasAdmin = data?.some((r) => r.role === 'admin') || false;
        setIsAdmin(hasAdmin);
      });
  }, [user]);

  return (
    <Routes>
      <Route path="/" element={<AppContent />} />
      <Route
        path="/admin/document-spaces"
        element={<SpaceManagement user={user} isAdmin={isAdmin} onBackToDashboard={() => navigate('/')} />}
      />
      <Route path="/:spaceSlug" element={<AppContent />} />
      <Route path="/:spaceSlug/folder/:folderId" element={<AppContent />} />
      <Route path="/:spaceSlug/file/:fileId" element={<AppContent />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <RouterGate>
          <RouteWrapper />
        </RouterGate>
      </BrowserRouter>
    </AuthProvider>
  );
}
